import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Statistic } from 'antd';
import moment from 'moment';
import { getStationSummary } from '../../modules/analytics';
import './reportForm.css';
import { ReactComponent as CO2Svg } from '../../static/icon_co2.svg';
import { ReactComponent as CoalSvg } from '../../static/icon_coal.svg';
import { ReactComponent as TreeSvg } from '../../static/icon_tree.svg';
import { ReactComponent as CertSvg } from '../../static/icon_cert.svg';
import Icon from '@ant-design/icons';
/**
 * ReportForm component uses site information and station summary
 * data to populate report fields.
 */
const ReportForm = () => {
  const dispatch = useDispatch();
  const inspectionTime = useSelector(state => state.analytics.inspectionTime);
  const stationId = inspectionTime.stationId;
  const siteInfo = inspectionTime.stationInfo || {};
  const summary = useSelector(
    state => (state.analytics.stationSummary || {})[stationId] || {}
  );

  useEffect(() => {
    if (stationId) {
      dispatch(getStationSummary(stationId));
    }
  }, [dispatch, stationId]);

  const price = siteInfo?.estimatedSolarEnergyUnitPrice || 0;
  const round = (n) => Math.round(Number(n) || 0);
  const toCard = obj => ({
    value: round(obj?.value),
    unit: 'kWh',
    subtitle:
      '≈ ¥ ' + ((obj?.value || 0) * price).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    change: obj?.delta != null ? round(obj.delta) : 0,
  });

  const stats = {
    daily: toCard(summary.daily || {}),
    monthly: toCard(summary.monthly || {}),
    yearly: toCard(summary.yearly || {}),
    lifetime: {
      value: round(summary.lifetime),
      unit: 'kWh',
      subtitle:
        '≈ ¥ ' + ((summary.lifetime || 0) * price).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
      change: '',
    },
    monthlyGridCost: {
      value: summary.monthlyGridCostValue || 0,
      unitPrice: summary.monthlyGridCostUnitPrice || price,
    },
    monthlySolarCost: {
      value: summary.monthlySolarCostValue || 0,
      unitPrice: summary.monthlySolarCostUnitPrice || price,
    },
    monthlySaving: {
      value: summary.monthlySavingValue || 0,
      unitPrice: summary.monthlySavingUnitPrice || price,
    },
    monthlyGridExpenditure: {
      value: summary.monthlyGridExpenditureValue || 0,
      unitPrice: summary.monthlyGridExpenditureUnitPrice || price,
    },
    emissionCO2Saved: summary.emissionCO2Saved || 0,
    emissionCoalSaved: summary.emissionCoalSaved || 0,
    emissionCO2SavedBESS: summary.emissionCO2SavedBESS || 0,
    emissionCoalSavedBESS: summary.emissionCoalSavedBESS || 0,
  };

  // Values for electricity statistics - using monthly stats
  const generated = stats.monthly.value;
  const consumed = summary.monthlyConsumed != null ? round(summary.monthlyConsumed) : generated;
  const utilization = generated ? Math.round((consumed / generated) * 100) : 0;

  return (
    <div className="reportForm">
      {/* Row 1: Report Header */}
      <div className="reportRow reportHeader">
        <div className="headerLeft">
          <h1 className="reportTitle">Solar Plant Monthly Report</h1>
          <div className="plantName">{siteInfo.stationName}</div>
          <div className="plantAddress">{siteInfo.addr}</div>
          <div className="reportDetailsInline">
            <div className="detailItem">
              <div className="detailLabel">Owner</div>
              <div className="detailValue">{siteInfo.userName}</div>
            </div>
            <div className="detailItem">
              <div className="detailLabel">Commercial Operation Date</div>
              <div className="detailValue">
                {siteInfo.commercialOperationDate
                  ? moment(siteInfo.commercialOperationDate).format('YYYY-MM-DD')
                  : ''}
              </div>
            </div>
            <div className="detailItem">
              <div className="detailLabel">Capacity</div>
              <div className="detailValue">{siteInfo.capacity} kW</div>
            </div>
          </div>
        </div>
        <div className="headerRight">
          <div className="reportLogo" />
          <div className="reportMonthLabel">{moment().format('MMM YYYY').toUpperCase()}</div>
        </div>
      </div>
      {/* Row 2: Electricity Statistics */}
      <div className="reportRow electricitySection">
        <div className="reportSectionHeader">
          <div className="sectionTitle">Electricity Statistics from Power Bureau</div>
          <div className="sectionPeriod">
            Period: {moment().startOf('month').format('YYYY.MM.DD')} - {moment().endOf('month').format('YYYY.MM.DD')}
          </div>
        </div>
        <div className="electricityStats">
          <Card className="statCard">
            <Statistic title="Solar Energy Generated" value={generated} suffix="kWh" />
            <p className="statChange"><strong>{stats.monthly.change} kWh</strong> from last month</p>
          </Card>
          <Card className="statCard">
            <Statistic title="Solar Energy Consumed" value={consumed} suffix="kWh" />
            <p className="statChange"><strong>{stats.monthly.change} kWh</strong> from last month</p>
          </Card>
          <Card className="statCard">
            <Statistic title="Self Utilization Ratio" value={utilization} suffix="%" />
            <p className="statChange"><strong>{utilization}%</strong> last month</p>
          </Card>
        </div>
      </div>
      {/* Row 3: Summary of Solar Plant Benefits */}
      <div className="reportRow benefitsSection">
        <div className="reportSectionHeader">
          <div className="sectionTitle">Summary of Solar Plant Benefits</div>
        </div>
        <div className="benefitsSummary">
          <Card className="benefitCard">
            <div className="benefitBody">
              <div className="benefitCardHeader">Estimated grid electricity purchase cost</div>
              <div className="benefitCardValue">¥{stats.monthlyGridCost.value}</div>
            </div>
            <div className="benefitCardFooter">Estimated electricity unit price ~ {stats.monthlyGridCost.unitPrice}¥/kWh</div>
          </Card>
          <div className='benefitSign'>-</div>
          <Card className="benefitCard">
            <div className="benefitBody">
              <div className="benefitCardHeader">Actual solar electricity purchase cost</div>
              <div className="benefitCardValue">¥{stats.monthlySolarCost.value}</div>
            </div>
            <div className="benefitCardFooter">Solar electricity unit price ~ {stats.monthlySolarCost.unitPrice}¥/kWh</div>
          </Card>
          <div className='benefitSign'>=</div>
          <Card className="benefitCard">
            <div className="benefitBody">
              <div className="benefitCardHeader green">Electricity cost saving this period</div>
              <div className="benefitCardValue green underline">¥{stats.monthlySaving.value}</div>
            </div>
            <div className="benefitCardFooter">Saving percentrage {stats.monthlySaving.unitPrice}¥/kWh</div>
          </Card>
          <div className='benefitSign vertical'></div>
          <Card className="benefitCard">
            <div className="benefitBody">
              <div className="benefitCardHeader green">Estimated grid electricity purchasement cost</div>
              <div className="benefitCardValue green">¥{stats.monthlyGridExpenditure.value}</div>
            </div>
            <div className="benefitCardFooter">Site online for <br/>1 year and 7 months</div>
          </Card>
        </div>
        <div className="co2Cards">
          <Card className="smallCard"><Icon component={CO2Svg} /> <div className="innerCard"><span>CO2 Emission Saved</span><div><b>{(siteInfo.co2EmissionSavedRatio * stats.monthly.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>tons</div></div></Card>
          <Card className="smallCard"><Icon component={CoalSvg} /><div className="innerCard"><span>Equiv. Coal Saved</span><div><b>{(siteInfo.standardCoalSavedRatio * stats.monthly.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</b>tons</div></div></Card>
          <Card className="smallCard"><Icon component={TreeSvg} /><div className="innerCard"><span>Tree Plant</span><div><b>{(siteInfo.treePlantingRatio * stats.monthly.value).toLocaleString(undefined)}</b></div></div></Card>
          <Card className="smallCard"><Icon component={CertSvg} /><div className="innerCard"><span>Equiv. Green Cert</span><div><b>{(siteInfo.equiGreenCertRatio * stats.monthly.value).toLocaleString(undefined)}</b></div></div></Card>
        </div>
      </div>
      {/* Row 4: Other Data & Remarks */}
      <div className="reportRow otherDataSection">
        <div className="reportSectionHeader">
          <div className="sectionTitle">Other Data & Remarks</div>
        </div>
        <div className="otherDataBoxes">
          <div className="dataCard">
            <div className="dataCardBody">
              <div className="dataCardTitle">Line Loss Value</div>
              <div className="dataCardValue">{summary.lineLossValue || 0}</div>
            </div>
          </div>
          <div className="dataCard">
            <div className="dataCardBody">
              <div className="dataCardTitle">PR Value</div>
              <div className="dataCardValue">{summary.prValue || 0}</div>
            </div>
          </div>
          <div className="dataCard">
            <div className="dataCardBody">
              <div className="dataCardTitle">Failure Rate</div>
              <div className="dataCardValue">{summary.failureRate || 0}%</div>
            </div>
          </div>
        </div>
        <div className="remarksText">
          <span className="remarksLabel">Remarks:</span> {summary.remarks || 'Concentrating more electricity usage between 10:00 and 14:00 can effectively save more on electricity costs.'}
        </div>
      </div>
    </div>
  );
};

export default ReportForm;
